import { useEffect, useMemo, useState, useRef } from "react"
import { Select as AntdSelect, Spin } from "antd"
import debounce from "lodash.debounce"
import apiClient from "../../Utils/apiClient"

const SelectBox = ({
  optionValue = null,
  name,
  endPoint,
  style,
  className,
  placeholder,
  onChange,
  onSelect,
  value = [],
  options: externalOptions = [],
  multiple = false,
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const searchTextRef = useRef("")
  const fullDataRef = useRef([])
  const pageSize = 10

  const fetchOptions = async (text = "", pageNo = 1) => {
    setLoading(true)
    try {
      const payload =
        endPoint === "devices/identifiers" ? { search: text } : { [name]: text }

      const response = await apiClient(
        `filter/${endPoint}?page=${pageNo}&pageSize=${pageSize}`,
        "POST",
        payload
      )

      if (response.success) {
        const formatted = response.result.map((item) => ({
          value: optionValue ? item[optionValue] : item._id,
          label: item[name],
        }))
        if (pageNo === 1) {
          fullDataRef.current = response.result
        } else {
          const uniqueNew = response.result.filter(
            (newItem) =>
              !fullDataRef.current.find(
                (existing) =>
                  existing[optionValue || "_id"] ===
                  newItem[optionValue || "_id"]
              )
          )
          fullDataRef.current = [...fullDataRef.current, ...uniqueNew]
        }

        setOptions((prev) =>
          pageNo === 1
            ? formatted
            : [
                ...prev,
                ...formatted.filter(
                  (f) => !prev.find((p) => p.value === f.value)
                ),
              ]
        )

        setHasMore(response.result.length === pageSize)
        setPage(pageNo)
      }
    } catch (error) {
      console.error("SelectBox fetch error:", error)
    } finally {
      setLoading(false)
    }
  }

  const debounceFetcher = useMemo(() => {
    return debounce((text) => {
      searchTextRef.current = text
      fetchOptions(text, 1) // reset page
    }, 300)
  }, [name, endPoint])

  useEffect(() => {
    fetchOptions("", 1)
  }, [])

  useEffect(() => () => debounceFetcher.cancel(), [debounceFetcher])

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loading) {
      fetchOptions(searchTextRef.current, page + 1)
    }
  }

  const combinedOptions = useMemo(() => {
    const map = new Map()
    ;[...externalOptions, ...options].forEach((opt) => map.set(opt.value, opt))
    return [...map.values()]
  }, [externalOptions, options])

  const handleChange = (selectedValue) => {
    if (multiple) {
      const selectedObjects = fullDataRef.current.filter((item) =>
        selectedValue.includes(item[optionValue || "_id"])
      )
      onChange?.(selectedValue, selectedObjects)
      onSelect?.(selectedValue, selectedObjects)
    } else {
      const selectedObject = fullDataRef.current.find(
        (item) => item[optionValue || "_id"] === selectedValue
      )
      onChange?.(selectedValue, selectedObject)
      onSelect?.(selectedValue, selectedObject)
    }
  }

  return (
    <AntdSelect
      mode={multiple ? "multiple" : undefined}
      showSearch
      filterOption={false}
      value={value}
      onSearch={debounceFetcher}
      onChange={handleChange}
      options={combinedOptions}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : "No results"}
      placeholder={placeholder}
      className={className}
      style={style || { width: 200 }}
      onPopupScroll={handlePopupScroll}
    />
  )
}

export default SelectBox
